import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import './CreateHealthProgramPage.css';

const generateQuestionId = () => `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const formatDateValue = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const normalizeLinkedFaqIds = (linkedFaqs) => {
  if (!Array.isArray(linkedFaqs)) return [];

  return linkedFaqs
    .map((faq) => {
      if (faq && typeof faq === 'object') {
        return faq._id || faq.id || '';
      }

      return faq || '';
    })
    .map((faqId) => String(faqId).trim())
    .filter(Boolean);
};

const normalizeProgramSections = (sections) => {
  if (!Array.isArray(sections)) return [];

  return sections.map((section, sectionIndex) => ({
    label: section?.label || section?.title || '',
    description: section?.description || '',
    weight: section?.weight || 50,
    questions: Array.isArray(section?.questions)
      ? section.questions.map((question, questionIndex) => ({
          label: question?.label || '',
          type: question?.type || 'text',
          weight: question?.weight || 10,
          required: Boolean(question?.required),
          options: Array.isArray(question?.options) ? question.options : [],
          optionsText: typeof question?.optionsText === 'string'
            ? question.optionsText
            : (Array.isArray(question?.options) ? question.options.join('\n') : ''),
          placeholder: question?.placeholder || '',
          helpText: question?.helpText || '',
          questionId: question?.questionId || `q_${sectionIndex}_${questionIndex}`,
          conditional: {
            enabled: Boolean(question?.conditional?.enabled),
            parentQuestionId: question?.conditional?.parentQuestionId || '',
            showWhenValue: question?.conditional?.showWhenValue ?? ''
          }
        }))
      : []
  }));
};

const buildProgramJsonPayload = (program = {}) => ({
  title: program.title ?? '',
  description: program.description ?? '',
  category: program.category ?? 'health',
  programType: Array.isArray(program.programType)
    ? program.programType
    : program.programType
      ? [program.programType]
      : [],
  focusAreas: Array.isArray(program.focusAreas)
    ? program.focusAreas
    : Array.isArray(program.healthFocusAreas)
      ? program.healthFocusAreas
      : [],
  targetPopulation: Array.isArray(program.targetPopulation) && program.targetPopulation.length > 0
    ? program.targetPopulation
    : ['All Ages'],
  eligibilityCriteria: program.eligibilityCriteria ?? '',
  fundingAmount: program.fundingAmount ?? '',
  fundingType: program.fundingType ?? 'Free Service',
  applicationDeadline: formatDateValue(program.applicationDeadline),
  applicationProcess: program.applicationProcess ?? '',
  requiredDocuments: Array.isArray(program.requiredDocuments) ? program.requiredDocuments : [],
  contactInfo: {
    name: program.contactInfo?.name ?? '',
    email: program.contactInfo?.email ?? '',
    phone: program.contactInfo?.phone ?? '',
    location: program.contactInfo?.location ?? '',
    hours: program.contactInfo?.hours ?? ''
  },
  isOngoing: Boolean(program.isOngoing),
  startDate: formatDateValue(program.startDate),
  endDate: formatDateValue(program.endDate),
  maxParticipants: program.maxParticipants ?? '',
  status: program.status ?? 'draft',
  isPublished: Boolean(program.isPublished),
  isFeatured: Boolean(program.isFeatured),
  visibleTo: Array.isArray(program.visibleTo) && program.visibleTo.length > 0
    ? program.visibleTo
    : ['public', 'user', 'citizen'],
  linkedFaqs: normalizeLinkedFaqIds(program.linkedFaqs),
  sections: normalizeProgramSections(program.sections)
});

const EditHealthProgramPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [programCategories, setProgramCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');

  const JSON_TEMPLATE = JSON.stringify({
    title: 'Community Mental Health Support Program',
    description: 'A program providing mental health resources and support to RSMIN community members.',
    category: 'health',
    programType: ['Counselling'],
    focusAreas: ['Mental Health'],
    targetPopulation: ['All Ages', 'Adults'],
    eligibilityCriteria: 'Must be a recognized RSMIN citizen or family member.',
    fundingAmount: 0,
    fundingType: 'Free Service',
    applicationDeadline: '2026-12-31',
    applicationProcess: 'Complete the online application and submit supporting documents.',
    requiredDocuments: ['Photo ID', 'Proof of RSMIN Citizenship'],
    contactInfo: {
      name: 'Program Coordinator',
      email: 'health@rsmin.ca',
      phone: '(800) 555-0100',
      location: 'RSMIN Community Centre',
      hours: 'Mon-Fri 9AM-5PM'
    },
    isOngoing: true,
    startDate: '',
    endDate: '',
    maxParticipants: '',
    isPublished: false,
    isFeatured: false,
    visibleTo: ['public', 'user', 'citizen'],
    sections: [
      {
        label: 'Eligibility & Background',
        description: 'Help us confirm your eligibility for this program.',
        weight: 50,
        questions: [
          {
            label: 'Are you a recognized RSMIN citizen or immediate family member?',
            type: 'radio',
            required: true,
            weight: 25,
            options: ['Yes', 'No'],
            placeholder: '',
            helpText: ''
          },
          {
            label: 'Please describe your current mental health support needs.',
            type: 'textarea',
            required: true,
            weight: 25,
            options: [],
            placeholder: 'Describe your needs...',
            helpText: 'This information is confidential and used only for program placement.'
          }
        ]
      },
      {
        label: 'Commitments & Consent',
        description: '',
        weight: 50,
        questions: [
          {
            label: 'I consent to participate in this program and understand its terms.',
            type: 'checkbox',
            required: true,
            weight: 50,
            options: [],
            placeholder: '',
            helpText: ''
          }
        ]
      }
    ]
  }, null, 2);

  const getCurrentCategory = () => {
    if (!formData) return { programTypes: [], focusAreas: [], label: '' };
    return programCategories.find(c => c.key === formData.category) || { programTypes: [], focusAreas: [], label: '' };
  };

  const programTypes = getCurrentCategory().programTypes || [];
  const programTypeOptions = programTypes.length > 0
    ? programTypes
    : (formData?.programType ? [formData.programType] : []);
  const focusAreas = getCurrentCategory().focusAreas || [];

  // Default values
  const defaultTargetPopulations = [
    'All Ages',
    'Children',
    'Youth',
    'Adults',
    'Elders',
    'Families',
    'Women',
    'Men',
    'Specific Conditions'
  ];

  const defaultFundingTypes = [
    'Grant',
    'Subsidy',
    'Free Service',
    'Sliding Scale',
    'Insurance Covered',
    'Scholarship',
    'Bursary',
    'One-time Payment',
    'Recurring Support'
  ];

  // Get target populations and funding types from category or use defaults
  const targetPopulations = (getCurrentCategory().targetPopulations && getCurrentCategory().targetPopulations.length > 0)
    ? getCurrentCategory().targetPopulations
    : defaultTargetPopulations;

  const fundingTypes = (getCurrentCategory().fundingTypes && getCurrentCategory().fundingTypes.length > 0)
    ? getCurrentCategory().fundingTypes
    : defaultFundingTypes;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        setProgramCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching program categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const fetchProgram = useCallback(async () => {
    try {
      const response = await api.get(`/api/health-programs/${id}`);
      const program = response.data.program;

      setFormData({
        ...program,
        ...buildProgramJsonPayload(program)
      });
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching program details');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  useEffect(() => {
    api.get('/api/faq/public?limit=200&sortBy=title&sortOrder=asc')
      .then(res => setFaqs(res.data.posts || []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFocusAreaChange = (area) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: (prev.focusAreas || []).includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...(prev.focusAreas || []), area]
    }));
  };

  const handleTargetPopulationChange = (pop) => {
    setFormData(prev => ({
      ...prev,
      targetPopulation: (prev.targetPopulation || ['All Ages']).includes(pop)
        ? prev.targetPopulation.filter(p => p !== pop)
        : [...(prev.targetPopulation || ['All Ages']), pop]
    }));
  };

  const handleVisibleToChange = (tier) => {
    setFormData(prev => ({
      ...prev,
      visibleTo: (prev.visibleTo || ['public', 'user', 'citizen']).includes(tier)
        ? prev.visibleTo.filter(t => t !== tier)
        : [...(prev.visibleTo || ['public', 'user', 'citizen']), tier]
    }));
  };

  const handleArrayFieldChange = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const handleJsonImport = () => {
    if (!formData) return;

    setJsonError('');

    try {
      const parsed = JSON.parse(jsonInput);
      const mergedProgram = buildProgramJsonPayload({
        ...formData,
        ...parsed,
        category: formData.category,
        programType: parsed.programType ?? formData.programType,
        focusAreas: parsed.focusAreas ?? parsed.healthFocusAreas ?? formData.focusAreas,
        targetPopulation: parsed.targetPopulation ?? formData.targetPopulation,
        requiredDocuments: parsed.requiredDocuments ?? formData.requiredDocuments,
        linkedFaqs: parsed.linkedFaqs ?? formData.linkedFaqs,
        visibleTo: parsed.visibleTo ?? formData.visibleTo,
        sections: parsed.sections ?? formData.sections,
        contactInfo: {
          ...(formData.contactInfo || {}),
          ...(parsed.contactInfo || {})
        }
      });

      setFormData((prev) => ({
        ...prev,
        ...mergedProgram,
        category: prev.category
      }));
      setShowJsonImport(false);
      setJsonInput('');
    } catch (err) {
      setJsonError('Invalid JSON — please check the format and try again.');
    }
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          label: '',
          description: '',
          weight: 50,
          questions: []
        }
      ]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const handleSectionChange = (index, field, value) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[index][field] = value;
      return { ...prev, sections };
    });
  };

  const addQuestion = (sectionIndex) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        questions: [
          ...sections[sectionIndex].questions,
          {
            label: '',
            type: 'text',
            weight: 10,
            required: false,
            options: [],
            optionsText: '',
            placeholder: '',
            helpText: '',
            questionId: generateQuestionId(),
            conditional: {
              enabled: false,
              parentQuestionId: '',
              showWhenValue: ''
            }
          }
        ]
      };
      return { ...prev, sections };
    });
  };

  const removeQuestion = (sectionIndex, questionIndex) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex].questions = sections[sectionIndex].questions.filter((_, i) => i !== questionIndex);
      return { ...prev, sections };
    });
  };

  const handleQuestionChange = (sectionIndex, questionIndex, field, value) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      sections[sectionIndex].questions[questionIndex][field] = value;
      return { ...prev, sections };
    });
  };

  const handleQuestionOptionsChange = (sectionIndex, questionIndex, value) => {
    const options = value
      .split('\n')
      .map(opt => opt.trim())
      .filter(Boolean);

    setFormData(prev => {
      const sections = [...prev.sections];
      const currentQuestion = sections[sectionIndex].questions[questionIndex] || {};

      sections[sectionIndex].questions[questionIndex] = {
        ...currentQuestion,
        options,
        optionsText: value
      };

      return { ...prev, sections };
    });
  };

  const handleOptionsTextareaKeyDown = (e, sectionIndex, questionIndex) => {
    if (e.key !== 'Enter') return;

    e.preventDefault();
    e.stopPropagation();

    const { value, selectionStart, selectionEnd } = e.target;
    const nextValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;

    handleQuestionOptionsChange(sectionIndex, questionIndex, nextValue);

    requestAnimationFrame(() => {
      const cursorPosition = selectionStart + 1;
      e.target.selectionStart = cursorPosition;
      e.target.selectionEnd = cursorPosition;
    });
  };

  const handleQuestionConditionalChange = (sectionIndex, questionIndex, field, value) => {
    setFormData(prev => {
      const sections = [...prev.sections];
      const currentQuestion = sections[sectionIndex].questions[questionIndex] || {};
      const currentConditional = {
        enabled: Boolean(currentQuestion?.conditional?.enabled),
        parentQuestionId: currentQuestion?.conditional?.parentQuestionId || '',
        showWhenValue: currentQuestion?.conditional?.showWhenValue ?? ''
      };

      const nextConditional = {
        ...currentConditional,
        [field]: value
      };

      if (field === 'enabled' && !value) {
        nextConditional.parentQuestionId = '';
        nextConditional.showWhenValue = '';
      }

      if (field === 'parentQuestionId' && value !== currentConditional.parentQuestionId) {
        nextConditional.showWhenValue = '';
      }

      sections[sectionIndex].questions[questionIndex] = {
        ...currentQuestion,
        conditional: nextConditional
      };

      return { ...prev, sections };
    });
  };

  const getQuestionReferences = (sections, currentSectionIndex, currentQuestionIndex) => {
    if (!Array.isArray(sections)) return [];

    return sections.flatMap((section, sectionIndex) =>
      (section.questions || [])
        .filter((_, questionIndex) => !(sectionIndex === currentSectionIndex && questionIndex === currentQuestionIndex))
        .map((question, questionIndex) => ({
          questionId: question.questionId || `q_${sectionIndex}_${questionIndex}`,
          label: question.label || `Question ${questionIndex + 1}`,
          type: question.type || 'text',
          options: Array.isArray(question.options) ? question.options : [],
          sectionLabel: section.label || `Section ${sectionIndex + 1}`
        }))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        sections: (formData.sections || []).map((section) => ({
          ...section,
          questions: (section.questions || []).map(({ optionsText, ...question }) => question)
        }))
      };

      await api.put(`/api/health-programs/${id}`, payload);
      navigate('/staff-health-programs');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating health program');
      setSaving(false);
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return (
      <div className="create-health-program-container">
        <div className="loading">Loading program details...</div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="create-health-program-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  const currentProgramJson = JSON.stringify(buildProgramJsonPayload(formData), null, 2);

  return (
    <div className="create-health-program-container">
      <div className="page-header">
        <h1>Edit Program</h1>
        <div className="header-actions">
          <button onClick={() => navigate('/staff-health-programs')} className="btn-back" type="button">
            ← Back to Programs
          </button>
          <button onClick={handleSubmit} className="btn-save" type="button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="json-import-bar">
        <button
          type="button"
          className="btn-json-toggle"
          onClick={() => {
            setShowJsonImport((value) => !value);
            setJsonError('');
          }}
        >
          {showJsonImport ? '✕ Close JSON Import' : '{ } Import from JSON'}
        </button>
        {!showJsonImport && (
          <span className="json-bar-hint">Copy the current program JSON, update it, and paste it back to reload the form. The category stays locked while editing.</span>
        )}
      </div>

      {showJsonImport && (
        <div className="json-import-panel">
          <div className="json-import-header">
            <h3>📋 Edit Program with JSON</h3>
            <p>Use the template or copy the current program JSON, make your updates, then paste it back and click <strong>Reload Form from JSON</strong>.</p>
          </div>

          <div className="json-template-block">
            <div className="json-template-label">
              <span>Example Template</span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-copy-json"
                  onClick={() => navigator.clipboard.writeText(JSON_TEMPLATE)}
                >
                  📋 Copy Template
                </button>
                <button
                  type="button"
                  className="btn-copy-json"
                  onClick={() => navigator.clipboard.writeText(currentProgramJson)}
                >
                  📋 Copy Current JSON
                </button>
              </div>
            </div>
            <pre className="json-template-pre">{JSON_TEMPLATE}</pre>
          </div>

          <div className="json-template-block">
            <div className="json-template-label">
              <span>Current Program JSON</span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-copy-json"
                  onClick={() => setJsonInput(currentProgramJson)}
                >
                  ✏ Load Current into Editor
                </button>
                <button
                  type="button"
                  className="btn-copy-json"
                  onClick={() => navigator.clipboard.writeText(currentProgramJson)}
                >
                  📋 Copy Current JSON
                </button>
              </div>
            </div>
            <pre className="json-template-pre">{currentProgramJson}</pre>
          </div>

          <div className="json-paste-block">
            <label htmlFor="edit-program-json-paste">Paste your updated JSON here:</label>
            <textarea
              id="edit-program-json-paste"
              className="json-paste-area"
              rows={14}
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setJsonError('');
              }}
              placeholder="Paste your JSON here..."
              spellCheck={false}
            />
          </div>

          {jsonError && <div className="json-error">{jsonError}</div>}

          <div className="json-import-actions">
            <button type="button" className="btn btn-primary" onClick={handleJsonImport} disabled={!jsonInput.trim()}>
              ⬇ Reload Form from JSON
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowJsonImport(false);
                setJsonInput('');
                setJsonError('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="program-form">
        {/* Basic Information Section */}
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label>Program Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Program Category *</label>
              <select
                name="category"
                value={formData.category}
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              >
                {programCategories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.icon} {category.label}
                  </option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                Category cannot be changed after creation
              </small>
            </div>

            <div className="form-group">
              <label>Program Type *</label>
              <div className="checkbox-group">
                {programTypeOptions.map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(formData.programType || []).includes(type)}
                      onChange={() => {
                        setFormData(prev => ({
                          ...prev,
                          programType: (prev.programType || []).includes(type)
                            ? prev.programType.filter(t => t !== type)
                            : [...(prev.programType || []), type]
                        }));
                      }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <RichTextEditor
              value={formData.description || ''}
              onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Target Population *</label>
              <div className="checkbox-group">
                {targetPopulations.map(pop => (
                  <label key={pop} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(formData.targetPopulation || ['All Ages']).includes(pop)}
                      onChange={() => handleTargetPopulationChange(pop)}
                    />
                    {pop}
                  </label>
                ))}
              </div>
            </div>

            {focusAreas.length > 0 && (
              <div className="form-group">
                <label>Focus Areas ({getCurrentCategory().label || 'Program'})</label>
                <div className="checkbox-grid">
                  {focusAreas.map(area => (
                    <label key={area} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.focusAreas?.includes(area)}
                        onChange={() => handleFocusAreaChange(area)}
                      />
                      {area}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Eligibility Section */}
        <div className="form-section">
          <h2>Eligibility & Requirements</h2>
          
          <div className="form-group">
            <label>Eligibility Criteria</label>
            <RichTextEditor
              value={formData.eligibilityCriteria || ''}
              onChange={(val) => setFormData(prev => ({ ...prev, eligibilityCriteria: val }))}
              placeholder="Describe who is eligible for this program..."
            />
          </div>

          <div className="form-group">
            <label>Application Process</label>
            <RichTextEditor
              value={formData.applicationProcess || ''}
              onChange={(val) => setFormData(prev => ({ ...prev, applicationProcess: val }))}
              placeholder="Describe how to apply..."
            />
          </div>

          <div className="form-group">
            <label>Required Documents (one per line)</label>
            <textarea
              value={(formData.requiredDocuments || []).join('\n')}
              onChange={(e) => handleArrayFieldChange('requiredDocuments', e.target.value)}
              rows={4}
              placeholder="Photo ID&#10;Proof of residency&#10;Medical records"
            />
          </div>

          {faqs.length > 0 && (
            <div className="form-group">
              <label>Linked FAQs</label>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 6 }}>Hold Ctrl / Cmd to select multiple. Selected FAQs will appear as links on this program's detail page.</p>
              <select
                multiple
                value={formData.linkedFaqs || []}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  linkedFaqs: Array.from(e.target.selectedOptions, o => o.value)
                }))}
                style={{ width: '100%', minHeight: 120 }}
              >
                {faqs.map(faq => (
                  <option key={faq._id} value={faq._id}>{faq.title}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Funding Section */}
        <div className="form-section">
          <h2>Funding Information</h2>
          
          <div className="form-group">
            <label>Funding Type</label>
            <select
              name="fundingType"
              value={formData.fundingType || 'Free Service'}
              onChange={handleChange}
            >
              {fundingTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Funding Amount ($)</label>
            <input
              type="number"
              name="fundingAmount"
              value={formData.fundingAmount || ''}
              onChange={handleChange}
              placeholder="Leave blank if not applicable"
            />
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="form-section">
          <h2>Contact Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="contactInfo.name"
                value={formData.contactInfo?.name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="contactInfo.email"
                value={formData.contactInfo?.email || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="contactInfo.phone"
                value={formData.contactInfo?.phone || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="contactInfo.location"
                value={formData.contactInfo?.location || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Hours of Operation</label>
            <input
              type="text"
              name="contactInfo.hours"
              value={formData.contactInfo?.hours || ''}
              onChange={handleChange}
              placeholder="e.g., Monday-Friday, 9AM-5PM"
            />
          </div>
        </div>

        {/* Schedule Section */}
        <div className="form-section">
          <h2>Program Schedule</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isOngoing"
                checked={formData.isOngoing || false}
                onChange={handleChange}
              />
              This is an ongoing program (no end date)
            </label>
          </div>

          {!formData.isOngoing && (
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate ? formData.startDate.split('T')[0] : ''}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate ? formData.endDate.split('T')[0] : ''}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Application Deadline</label>
              <input
                type="date"
                name="applicationDeadline"
                value={formData.applicationDeadline ? formData.applicationDeadline.split('T')[0] : ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Maximum Participants</label>
              <input
                type="number"
                name="maxParticipants"
                value={formData.maxParticipants || ''}
                onChange={handleChange}
                placeholder="Leave blank for unlimited"
              />
            </div>
          </div>
        </div>

        {/* Application Form Sections */}
        <div className="form-section">
          <h2>Application Form Builder</h2>
          <p className="section-help">Build the application form that users will fill out</p>
          
          {formData.sections && formData.sections.map((section, sIdx) => (
            <div key={sIdx} className="section-builder">
              <div className="section-header">
                <h3>Section {sIdx + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeSection(sIdx)}
                  className="btn-remove"
                >
                  Remove Section
                </button>
              </div>

              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  value={section.label}
                  onChange={(e) => handleSectionChange(sIdx, 'label', e.target.value)}
                  placeholder="e.g., Personal Information"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Section Description</label>
                  <textarea
                    value={section.description}
                    onChange={(e) => handleSectionChange(sIdx, 'description', e.target.value)}
                    rows={2}
                    placeholder="Optional description for this section"
                  />
                </div>

                <div className="form-group">
                  <label>Section Weight (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={section.weight || 50}
                    onChange={(e) => handleSectionChange(sIdx, 'weight', parseInt(e.target.value) || 0)}
                    placeholder="50"
                  />
                </div>
              </div>

              <div className="questions-container">
                <h4>Questions</h4>
                {section.questions && section.questions.map((question, qIdx) => (
                  <div key={qIdx} className="question-builder">
                    <div className="question-header">
                      <span>Question {qIdx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeQuestion(sIdx, qIdx)}
                        className="btn-remove-small"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Question Label</label>
                        <input
                          type="text"
                          value={question.label}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'label', e.target.value)}
                          placeholder="e.g., What is your name?"
                        />
                      </div>

                      <div className="form-group">
                        <label>Question Type</label>
                        <select
                          value={question.type}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'type', e.target.value)}
                        >
                          <option value="text">Short Text</option>
                          <option value="textarea">Long Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="multi-select">Multiple Choice</option>
                          <option value="file">File Upload</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Weight (0-100)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={question.weight || 10}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'weight', parseInt(e.target.value) || 0)}
                          placeholder="10"
                        />
                      </div>
                    </div>

                    {(question.type === 'dropdown' || question.type === 'multi-select') && (
                      <div className="form-group">
                        <label>Options (one per line)</label>
                        <textarea
                          value={question.optionsText ?? (question.options || []).join('\n')}
                          onChange={(e) => handleQuestionOptionsChange(sIdx, qIdx, e.target.value)}
                          onKeyDown={(e) => handleOptionsTextareaKeyDown(e, sIdx, qIdx)}
                          rows={3}
                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={Boolean(question?.conditional?.enabled)}
                          onChange={(e) => handleQuestionConditionalChange(sIdx, qIdx, 'enabled', e.target.checked)}
                        />
                        Show this question only when another question has a specific answer
                      </label>
                    </div>

                    {Boolean(question?.conditional?.enabled) && (
                      <>
                        {(() => {
                          const questionRefs = getQuestionReferences(formData.sections, sIdx, qIdx);
                          const selectedParent = questionRefs.find(ref => ref.questionId === (question?.conditional?.parentQuestionId || ''));

                          return (
                            <>
                              <div className="form-group">
                                <label>Parent Question *</label>
                                <select
                                  value={question?.conditional?.parentQuestionId || ''}
                                  onChange={(e) => handleQuestionConditionalChange(sIdx, qIdx, 'parentQuestionId', e.target.value)}
                                  required
                                >
                                  <option value="">-- Select parent question --</option>
                                  {questionRefs.map(ref => (
                                    <option key={ref.questionId} value={ref.questionId}>
                                      {ref.sectionLabel}: {ref.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {selectedParent && (
                                <div className="form-group">
                                  <label>Show When Parent Answer Is *</label>

                                  {selectedParent.type === 'checkbox' ? (
                                    <select
                                      value={question?.conditional?.showWhenValue ?? ''}
                                      onChange={(e) => handleQuestionConditionalChange(sIdx, qIdx, 'showWhenValue', e.target.value)}
                                      required
                                    >
                                      <option value="">-- Select value --</option>
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  ) : (selectedParent.type === 'dropdown' || selectedParent.type === 'multi-select') && selectedParent.options.length > 0 ? (
                                    <select
                                      value={question?.conditional?.showWhenValue ?? ''}
                                      onChange={(e) => handleQuestionConditionalChange(sIdx, qIdx, 'showWhenValue', e.target.value)}
                                      required
                                    >
                                      <option value="">-- Select option --</option>
                                      {selectedParent.options.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={question?.conditional?.showWhenValue ?? ''}
                                      onChange={(e) => handleQuestionConditionalChange(sIdx, qIdx, 'showWhenValue', e.target.value)}
                                      placeholder="Exact answer text that should trigger this question"
                                      required
                                    />
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>Placeholder Text</label>
                        <input
                          type="text"
                          value={question.placeholder || ''}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'placeholder', e.target.value)}
                          placeholder="Optional placeholder"
                        />
                      </div>

                      <div className="form-group">
                        <label>Help Text</label>
                        <input
                          type="text"
                          value={question.helpText || ''}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'helpText', e.target.value)}
                          placeholder="Optional help text"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={question.required || false}
                          onChange={(e) => handleQuestionChange(sIdx, qIdx, 'required', e.target.checked)}
                        />
                        Required field
                      </label>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addQuestion(sIdx)}
                  className="btn-add"
                >
                  + Add Another Question
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addSection}
            className="btn-add-section"
          >
            + Add Section
          </button>
        </div>

        {/* Publishing Section */}
        <div className="form-section">
          <h2>Publishing Options</h2>
          
          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished || false}
                onChange={handleChange}
              />
              Published (visible to public)
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured || false}
                onChange={handleChange}
              />
              Featured program
            </label>
          </div>

          <div className="form-group">
            <label>Visible To (Access Levels)</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(formData.visibleTo || ['public', 'user', 'citizen']).includes('public')}
                  onChange={() => handleVisibleToChange('public')}
                />
                🌍 Public (Not logged in visitors)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(formData.visibleTo || ['public', 'user', 'citizen']).includes('user')}
                  onChange={() => handleVisibleToChange('user')}
                />
                👤 User (Logged in, not verified)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(formData.visibleTo || ['public', 'user', 'citizen']).includes('citizen')}
                  onChange={() => handleVisibleToChange('citizen')}
                />
                ✓ Citizen (Verified with card number)
              </label>
            </div>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              Select which user tiers can view this program. Leave all checked for public access.
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/staff-health-programs')}
            className="btn-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-submit"
          >
            {saving ? 'Saving Changes...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditHealthProgramPage;
